import { isProduction } from 'js/sign-components/common/ts-utils';
import { getPAPUser } from 'js/sign-components/common/pap-user-store';

const STORAGE_KEY = 'papEventLoggerEnabled';
const EVENTS_STORAGE_KEY = 'papEventLoggerEvents';
const EVENTS_CHANGED_EVENT = 'pap-event-logger-events-changed';
export const PAP_EVENT_LOG_LIMIT = 200;

export type PAPEventLogEntry = {
  timestamp: number;
  eventName: string;
  userId: string | number | null | undefined;
  foreignUserId: string | number | null | undefined;
  properties: unknown;
  propertiesSerializationFailed?: boolean;
};

type StoredEventLog = {
  owner: string;
  events: PAPEventLogEntry[];
};

function readStoredState(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

let isEnabled = !isProduction() && readStoredState();

function isPAPEventLogEntry(value: unknown): value is PAPEventLogEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const entry = value as Partial<PAPEventLogEntry>;
  return (
    typeof entry.eventName === 'string' && typeof entry.timestamp === 'number'
  );
}

function readStoredEvents(owner: string): PAPEventLogEntry[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const storedEvents = window.sessionStorage.getItem(EVENTS_STORAGE_KEY);
    const parsedLog: unknown = storedEvents ? JSON.parse(storedEvents) : null;
    if (
      typeof parsedLog !== 'object' ||
      parsedLog === null ||
      (parsedLog as StoredEventLog).owner !== owner ||
      !Array.isArray((parsedLog as StoredEventLog).events)
    ) {
      return [];
    }
    return (parsedLog as StoredEventLog).events
      .filter(isPAPEventLogEntry)
      .slice(0, PAP_EVENT_LOG_LIMIT);
  } catch {
    return [];
  }
}

let currentOwner: string | null | undefined;
let eventLogs: PAPEventLogEntry[] = [];

function getCurrentAccountGuid(
  entry?: PAPEventLogEntry,
): string | null | undefined {
  const toolbar =
    typeof document === 'undefined'
      ? null
      : document.getElementById('hs-debug-tools');
  if (toolbar) {
    return toolbar.dataset.accountGuid || null;
  }

  const guid = getPAPUser()?.guid ?? entry?.foreignUserId;
  return guid !== null && guid !== undefined ? String(guid) : undefined;
}

function notifyEventsChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EVENTS_CHANGED_EVENT));
  }
}

function persistEvents(): void {
  if (typeof window === 'undefined' || !currentOwner) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      EVENTS_STORAGE_KEY,
      JSON.stringify({
        owner: currentOwner,
        events: eventLogs,
      } as StoredEventLog),
    );
  } catch {
    // Keep the in-memory log available when storage is blocked or full.
  }
}

function syncOwner(owner: string | null): void {
  if (owner === currentOwner) {
    return;
  }

  currentOwner = owner;
  eventLogs = owner ? readStoredEvents(owner) : [];
  if (typeof window !== 'undefined' && !eventLogs.length) {
    try {
      window.sessionStorage.removeItem(EVENTS_STORAGE_KEY);
    } catch {
      // The in-memory log remains isolated when storage is blocked.
    }
  }
}

export function isPAPEventLoggerEnabled(): boolean {
  return !isProduction() && isEnabled;
}

export function setPAPEventLoggerEnabled(enabled: boolean): void {
  if (isProduction()) {
    isEnabled = false;
    return;
  }

  isEnabled = enabled;
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (enabled) {
      window.sessionStorage.setItem(STORAGE_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Keep the in-memory toggle usable when browser storage is blocked.
  }
}

export function getPAPEventLogs(): PAPEventLogEntry[] {
  const owner = getCurrentAccountGuid();
  if (owner === undefined) {
    return [];
  }
  syncOwner(owner);
  return eventLogs;
}

export function addPAPEventLog(entry: PAPEventLogEntry): void {
  if (
    !isPAPEventLoggerEnabled() ||
    entry.eventName === 'eng.render_react_component'
  ) {
    return;
  }

  const owner = getCurrentAccountGuid(entry);
  if (owner === undefined) {
    return;
  }
  syncOwner(owner);
  if (!owner) {
    return;
  }

  let properties = entry.properties;
  let propertiesSerializationFailed = false;
  try {
    properties = JSON.parse(JSON.stringify(properties ?? {}));
  } catch {
    properties = null;
    propertiesSerializationFailed = true;
  }

  eventLogs = [
    {
      ...entry,
      properties,
      ...(propertiesSerializationFailed
        ? { propertiesSerializationFailed }
        : {}),
    },
    ...eventLogs,
  ].slice(0, PAP_EVENT_LOG_LIMIT);
  persistEvents();
  notifyEventsChanged();
}

export function clearPAPEventLogs(): void {
  currentOwner = undefined;
  eventLogs = [];
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(EVENTS_STORAGE_KEY);
    } catch {
      // The in-memory log is still cleared when storage is blocked.
    }
  }
  notifyEventsChanged();
}

export function subscribeToPAPEventLogs(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener(EVENTS_CHANGED_EVENT, listener);
  return () => window.removeEventListener(EVENTS_CHANGED_EVENT, listener);
}
